import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { useMinistry } from '@/contexts/MinistryContext';
import { toast } from 'sonner';
import { Plus, Users, Wand2, Eye, Trash2, UserPlus, UserMinus, Edit, X as XIcon, Check, Save, Search, AlertTriangle, UserCheck, Star, Calendar, CheckCircle2, Download } from 'lucide-react';
import PGAttendance from '@/components/PGAttendance';
import EditPreviewGroup from '@/components/EditPreviewGroup';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Helper to build full image URL for photos
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/uploads') || url.startsWith('/uploads')) {
    const baseUrl = API.replace('/api', '');
    const path = url.startsWith('/api/') ? url : `/api${url}`;
    return `${baseUrl}${path}`;
  }
  return url;
};

const SmallGroups = () => {
  const { user } = useContext(AuthContext);
  const { currentMinistry, loading: ministryLoading } = useMinistry();
  const [groups, setGroups] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [adolescents, setAdolescents] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dashboard stats
  const [pgStats, setPgStats] = useState({
    totalPGs: 0,
    pgsMeninos: 0,
    pgsMeninas: 0,
    adolescentesEmPG: 0,
    adolescentesSemPG: 0,
    totalAtivos: 0,
    totalAtivosDesengajados: 0
  });
  
  // Dialogs
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [volunteerDialogOpen, setVolunteerDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [viewGroupDialogOpen, setViewGroupDialogOpen] = useState(false);
  
  // Forms
  const [newGroup, setNewGroup] = useState({ name: '', gender: 'masculino' });
  const [newVolunteer, setNewVolunteer] = useState({ name: '', phone: '', email: '', age: null, gender: 'masculino' });
  const [preview, setPreview] = useState([]);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [editingPreviewIndex, setEditingPreviewIndex] = useState(null);
  const [editPreviewDialogOpen, setEditPreviewDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupDetails, setSelectedGroupDetails] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  
  // Filtros
  const [genderFilter, setGenderFilter] = useState('all');
  const [ageRangeFilter, setAgeRangeFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [leaderStatusFilter, setLeaderStatusFilter] = useState('all'); // 'all', 'com_lider', 'sem_lider'
  const [semPgSearchFilter, setSemPgSearchFilter] = useState('');
  const [semPgGenderFilter, setSemPgGenderFilter] = useState('all');
  
  // Search states for add leader/member
  const [leaderSearchModal, setLeaderSearchModal] = useState('');
  const [memberSearchModal, setMemberSearchModal] = useState('');
  
  // Edit PG states
  const [editGroupDialogOpen, setEditGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editLeaderSearch, setEditLeaderSearch] = useState('');
  const [editMemberSearch, setEditMemberSearch] = useState('');
  const [editFacilitatorSearch, setEditFacilitatorSearch] = useState('');
  const [autoGenConfig, setAutoGenConfig] = useState({
    min_members_per_group: 4,
    max_members_per_group: 8,
    respect_affinity: true,
    affinity_priority: true,
    separate_by_gender: true,
    min_age: null,
    max_age: null,
    engagement_filter: ['ativo', 'desengajado', 'inativo']
  });

  // Controle de Imersão states
  const [pendingImmersion, setPendingImmersion] = useState([]);
  const [selectedForImmersion, setSelectedForImmersion] = useState([]);
  const [immersionDate, setImmersionDate] = useState('');
  const [immersionSearchFilter, setImmersionSearchFilter] = useState('');
  const [immersionGenderFilter, setImmersionGenderFilter] = useState('all');
  const [markImmersionDialogOpen, setMarkImmersionDialogOpen] = useState(false);
  const [immersionEngagementFilter, setImmersionEngagementFilter] = useState('ativo'); // ativo por padrão

  useEffect(() => {
    if (!ministryLoading && currentMinistry?.id) {
      fetchData();
    } else if (!ministryLoading && !currentMinistry) {
      setLoading(false);
    }
  }, [currentMinistry?.id, ministryLoading]);

  const fetchData = async () => {
    if (!currentMinistry?.id) return;
    try {
      const [groupsRes, volunteersRes, adolescentsRes, pendingImmersionRes, serviceAreasRes] = await Promise.all([
        axios.get(`${API}/small-groups?ministry_id=${currentMinistry.id}`),
        axios.get(`${API}/volunteers?ministry_id=${currentMinistry.id}`),
        axios.get(`${API}/adolescents?ministry_id=${currentMinistry.id}`),
        axios.get(`${API}/adolescents/pending-immersion?ministry_id=${currentMinistry.id}`),
        axios.get(`${API}/service-areas?ministry_id=${currentMinistry.id}`)
      ]);
      setGroups(groupsRes.data);
      setVolunteers(volunteersRes.data);
      setAdolescents(adolescentsRes.data);
      setPendingImmersion(pendingImmersionRes.data);
      setServiceAreas(serviceAreasRes.data);
      
      // Calculate PG stats
      calculatePGStats(groupsRes.data, adolescentsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const calculatePGStats = (groupsData, adolescentsData) => {
    // Separar PGs regulares dos temporários/visitantes
    // Considera como visitantes: PGs com is_temporary=true OU com "visitante" no nome
    const isVisitantesPG = (g) => g.is_temporary || g.name?.toLowerCase().includes('visitante');
    const regularPGs = groupsData.filter(g => !isVisitantesPG(g));
    const temporaryPGs = groupsData.filter(g => isVisitantesPG(g));
    
    // Total de PGs (apenas regulares)
    const totalPGs = regularPGs.length;
    
    // PGs por gênero (apenas regulares)
    const pgsMeninos = regularPGs.filter(g => g.gender === 'masculino').length;
    const pgsMeninas = regularPGs.filter(g => g.gender === 'feminino').length;
    
    // IDs de todos os membros em PGs REGULARES (não conta visitantes/temporários)
    const membersInRegularPGs = new Set();
    regularPGs.forEach(group => {
      (group.member_ids || []).forEach(id => membersInRegularPGs.add(id));
    });
    
    // IDs de membros em PGs de Visitantes
    const membersInVisitantesPGs = new Set();
    temporaryPGs.forEach(group => {
      (group.member_ids || []).forEach(id => membersInVisitantesPGs.add(id));
    });
    
    // Filtrar apenas ATIVOS para contagem de em PG / sem PG
    const ativos = adolescentsData.filter(a => a.engagement_status === 'ativo');
    
    // Contar adolescentes ATIVOS em PG REGULAR
    const adolescentesEmPG = ativos.filter(a => membersInRegularPGs.has(a.id)).length;
    
    // Adolescentes em PG de Visitantes (considerados "sem PG" pois aguardam alocação)
    const adolescentesEmVisitantes = ativos.filter(a => 
      membersInVisitantesPGs.has(a.id) && !membersInRegularPGs.has(a.id)
    ).length;
    
    // Adolescentes sem nenhum PG
    const adolescentesSemNenhumPG = ativos.filter(a => 
      !membersInRegularPGs.has(a.id) && !membersInVisitantesPGs.has(a.id)
    ).length;
    
    // Total "Sem PG" = visitantes + sem nenhum
    const adolescentesSemPG = adolescentesEmVisitantes + adolescentesSemNenhumPG;
    
    // Total de ativos + desengajados
    const ativosDesengajados = adolescentsData.filter(a => 
      a.engagement_status === 'ativo' || a.engagement_status === 'desengajado'
    );
    
    setPgStats({
      totalPGs,
      pgsMeninos,
      pgsMeninas,
      adolescentesEmPG,
      adolescentesSemPG,
      adolescentesEmVisitantes,
      adolescentesSemNenhumPG,
      totalAtivos: ativos.length,
      totalAtivosDesengajados: ativosDesengajados.length
    });
  };

  // Funções de Controle de Imersão
  const handleToggleImmersionSelection = (adolescentId) => {
    setSelectedForImmersion(prev => 
      prev.includes(adolescentId) 
        ? prev.filter(id => id !== adolescentId)
        : [...prev, adolescentId]
    );
  };

  const handleSelectAllForImmersion = (filtered) => {
    const allIds = filtered.map(a => a.id);
    const allSelected = allIds.every(id => selectedForImmersion.includes(id));
    if (allSelected) {
      setSelectedForImmersion(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedForImmersion(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleMarkImmersion = async () => {
    if (selectedForImmersion.length === 0) {
      toast.error('Selecione pelo menos um adolescente');
      return;
    }
    if (!immersionDate) {
      toast.error('Informe a data da imersão');
      return;
    }
    try {
      await axios.post(`${API}/adolescents/mark-immersion`, {
        adolescent_ids: selectedForImmersion,
        immersion_date: immersionDate
      });
      toast.success(`${selectedForImmersion.length} adolescente(s) marcado(s) como tendo completado a imersão!`);
      setSelectedForImmersion([]);
      setImmersionDate('');
      setMarkImmersionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao marcar imersão');
    }
  };

  const handleInitializeTemporaryPGs = async () => {
    try {
      const response = await axios.post(`${API}/small-groups/initialize-temporary?ministry_id=${currentMinistry?.id}`);
      if (response.data.created?.length > 0) {
        toast.success(response.data.message);
        fetchData();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Erro ao criar PGs temporários');
    }
  };

  // Função para exportar lista de imersão
  const handleExportImmersionList = (filteredList, format = 'xlsx') => {
    if (filteredList.length === 0) {
      toast.error('Nenhum adolescente para exportar');
      return;
    }

    const exportData = filteredList.map(a => ({
      'Nome': a.name,
      'Idade': a.age,
      'Gênero': a.gender === 'masculino' ? 'Masculino' : 'Feminino',
      'Telefone': a.phone || '',
      'Status': a.status,
      'Engajamento': a.engagement_status,
      'Total Check-ins': a.total_checkins || 0
    }));

    if (format === 'xlsx') {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pendentes Imersão');
      
      // Ajusta largura das colunas
      ws['!cols'] = [
        { wch: 35 }, // Nome
        { wch: 8 },  // Idade
        { wch: 12 }, // Gênero
        { wch: 18 }, // Telefone
        { wch: 12 }, // Status
        { wch: 14 }, // Engajamento
        { wch: 15 }  // Check-ins
      ];
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `pendentes_imersao_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(`${filteredList.length} registros exportados para Excel!`);
    } else {
      // CSV
      const headers = Object.keys(exportData[0]).join(',');
      const rows = exportData.map(row => Object.values(row).map(v => `"${v}"`).join(','));
      const csv = [headers, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `pendentes_imersao_${new Date().toISOString().split('T')[0]}.csv`);
      toast.success(`${filteredList.length} registros exportados para CSV!`);
    }
  };

  const handleCreateVolunteer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/volunteers?ministry_id=${currentMinistry?.id}`, newVolunteer);
      toast.success('Voluntário cadastrado!');
      setVolunteerDialogOpen(false);
      setNewVolunteer({ name: '', phone: '', email: '', age: null, gender: 'masculino' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao cadastrar voluntário');
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/small-groups?ministry_id=${currentMinistry?.id}`, newGroup);
      toast.success('Pequeno Grupo criado!');
      setGroupDialogOpen(false);
      setNewGroup({ name: '', gender: 'masculino' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar grupo');
    }
  };

  const handleAutoGenerate = async () => {
    try {
      const response = await axios.post(`${API}/small-groups/auto-generate`, autoGenConfig);
      setPreview(response.data.preview);
      setPreviewMeta({
        candidates_count: response.data.candidates_count,
        candidate_criteria: response.data.candidate_criteria
      });
      setConfigDialogOpen(false);
      setPreviewDialogOpen(true);
      if (response.data.preview.length === 0) {
        toast.info('Nenhum membro disponível para criar grupos automaticamente');
      }
    } catch (error) {
      toast.error('Erro ao gerar preview');
    }
  };

  const handleCreateFromPreview = async () => {
    try {
      await axios.post(`${API}/small-groups/create-from-preview`, preview);
      toast.success(`${preview.length} grupos criados com sucesso!`);
      setPreviewDialogOpen(false);
      setPreview([]);
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar grupos');
    }
  };

  // Salvar um único grupo do preview
  const handleSaveSinglePreviewGroup = async (index) => {
    try {
      const groupToSave = preview[index];
      await axios.post(`${API}/small-groups/save-single-from-preview`, groupToSave);
      toast.success(`Grupo "${groupToSave.suggested_name || groupToSave.name}" salvo com sucesso!`);
      // Remove o grupo salvo do preview
      setPreview(preview.filter((_, i) => i !== index));
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar grupo');
    }
  };

  const handleEditPreviewGroup = (index) => {
    setEditingPreviewIndex(index);
    setEditPreviewDialogOpen(true);
  };

  const handleUpdatePreviewGroup = (updatedGroup) => {
    const newPreview = [...preview];
    newPreview[editingPreviewIndex] = {
      ...updatedGroup,
      member_count: updatedGroup.members.length
    };
    setPreview(newPreview);
    setEditPreviewDialogOpen(false);
    toast.success('Grupo atualizado no preview!');
  };

  const handleRemovePreviewGroup = (index) => {
    if (window.confirm('Remover este grupo do preview?')) {
      setPreview(preview.filter((_, i) => i !== index));
      toast.success('Grupo removido do preview');
    }
  };

  const handleViewGroup = async (group) => {
    try {
      const response = await axios.get(`${API}/small-groups/${group.id}`);
      setSelectedGroupDetails(response.data);
      setSelectedGroup(group);
      setLeaderSearchModal('');
      setMemberSearchModal('');
      setViewGroupDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do grupo');
    }
  };

  // Abrir modal de edição
  const handleEditGroup = async (group) => {
    const response = await axios.get(`${API}/small-groups/${group.id}`);
    setEditingGroup({
      ...response.data,
      leader_ids: response.data.leader_ids || [],
      facilitator_ids: response.data.facilitator_ids || [],
      member_ids: response.data.member_ids || []
    });
    setEditLeaderSearch('');
    setEditMemberSearch('');
    setEditGroupDialogOpen(true);
  };

  // Salvar edições do PG
  const handleSaveEditedGroup = async () => {
    try {
      await axios.put(`${API}/small-groups/${editingGroup.id}`, {
        name: editingGroup.name,
        gender: editingGroup.gender,
        member_ids: editingGroup.member_ids,
        leader_ids: editingGroup.leader_ids,
        facilitator_ids: editingGroup.facilitator_ids,
        status: editingGroup.status
      });
      toast.success('Grupo atualizado com sucesso!');
      setEditGroupDialogOpen(false);
      setEditingGroup(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar grupo');
    }
  };

  // Toggle líder no grupo em edição
  const toggleEditLeader = (leaderId) => {
    const isSelected = editingGroup.leader_ids.includes(leaderId);
    setEditingGroup({
      ...editingGroup,
      leader_ids: isSelected
        ? editingGroup.leader_ids.filter(id => id !== leaderId)
        : [...editingGroup.leader_ids, leaderId]
    });
  };

  // Toggle facilitador no grupo em edição
  const toggleEditFacilitator = (facilitatorId) => {
    const isSelected = editingGroup.facilitator_ids?.includes(facilitatorId);
    setEditingGroup({
      ...editingGroup,
      facilitator_ids: isSelected
        ? (editingGroup.facilitator_ids || []).filter(id => id !== facilitatorId)
        : [...(editingGroup.facilitator_ids || []), facilitatorId]
    });
  };

  // Adicionar membro no grupo em edição
  const addEditMember = (adolescent) => {
    if (!editingGroup.member_ids.includes(adolescent.id)) {
      setEditingGroup({
        ...editingGroup,
        member_ids: [...editingGroup.member_ids, adolescent.id],
        members: [...(editingGroup.members || []), adolescent]
      });
    }
    setEditMemberSearch('');
  };

  // Remover membro no grupo em edição
  const removeEditMember = (memberId) => {
    setEditingGroup({
      ...editingGroup,
      member_ids: editingGroup.member_ids.filter(id => id !== memberId),
      members: (editingGroup.members || []).filter(m => m.id !== memberId)
    });
  };

  const handleAddMemberToGroup = async (memberId) => {
    if (!selectedGroup) return;
    try {
      await axios.post(`${API}/small-groups/${selectedGroup.id}/add-member?member_id=${memberId}`);
      toast.success('Membro adicionado!');
      handleViewGroup(selectedGroup);
    } catch (error) {
      toast.error('Erro ao adicionar membro');
    }
  };

  const handleRemoveMemberFromGroup = async (memberId) => {
    if (!selectedGroup) return;
    try {
      await axios.delete(`${API}/small-groups/${selectedGroup.id}/remove-member/${memberId}`);
      toast.success('Membro removido!');
      handleViewGroup(selectedGroup);
    } catch (error) {
      toast.error('Erro ao remover membro');
    }
  };

  const handleAddLeaderToGroup = async (leaderId) => {
    if (!selectedGroup) return;
    try {
      await axios.post(`${API}/small-groups/${selectedGroup.id}/add-leader?leader_id=${leaderId}`);
      toast.success('Líder adicionado!');
      handleViewGroup(selectedGroup);
    } catch (error) {
      toast.error('Erro ao adicionar líder');
    }
  };

  const handleRemoveLeaderFromGroup = async (leaderId) => {
    if (!selectedGroup) return;
    try {
      await axios.delete(`${API}/small-groups/${selectedGroup.id}/remove-leader/${leaderId}`);
      toast.success('Líder removido!');
      handleViewGroup(selectedGroup);
    } catch (error) {
      toast.error('Erro ao remover líder');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (window.confirm('Tem certeza que deseja excluir este grupo?')) {
      try {
        await axios.delete(`${API}/small-groups/${groupId}`);
        toast.success('Grupo excluído!');
        fetchData();
      } catch (error) {
        toast.error('Erro ao excluir grupo');
      }
    }
  };

  return (
    <div className="fade-in" data-testid="small-groups-page">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Space Grotesk'}}>Pequenos Grupos</h1>
        <p className="text-lg text-gray-600">Gerencie PGs e voluntários do ministério</p>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="card p-4 text-center bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <p className="text-3xl font-bold text-purple-700">{pgStats.totalPGs}</p>
          <p className="text-sm text-purple-600 font-medium">Total de PGs</p>
        </div>
        <div className="card p-4 text-center bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <p className="text-3xl font-bold text-blue-700">{pgStats.pgsMeninos}</p>
          <p className="text-sm text-blue-600 font-medium">👦 PGs Meninos</p>
        </div>
        <div className="card p-4 text-center bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
          <p className="text-3xl font-bold text-pink-700">{pgStats.pgsMeninas}</p>
          <p className="text-sm text-pink-600 font-medium">👧 PGs Meninas</p>
        </div>
        <div className="card p-4 text-center bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <p className="text-3xl font-bold text-green-700">{pgStats.adolescentesEmPG}</p>
          <p className="text-sm text-green-600 font-medium">✓ Ativos em PG</p>
        </div>
        <div className="card p-4 text-center bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <p className="text-3xl font-bold text-amber-700">{pgStats.adolescentesSemPG}</p>
          <p className="text-sm text-amber-600 font-medium">⚠ Ativos sem PG</p>
        </div>
        <div className="card p-4 text-center bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <p className="text-3xl font-bold text-gray-700">{pgStats.totalAtivosDesengajados}</p>
          <p className="text-sm text-gray-600 font-medium">Ativos + Deseng.</p>
        </div>
      </div>

      <Tabs defaultValue="groups" className="mb-6">
        <TabsList>
          <TabsTrigger value="groups">Grupos</TabsTrigger>
          <TabsTrigger value="sem-pg" className="relative">
            Sem PG
            {pgStats.adolescentesSemPG > 0 && (
              <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {pgStats.adolescentesSemPG}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="immersion" className="relative">
            Imersão
            {(() => {
              const ativosAguardando = pendingImmersion.filter(a => a.engagement_status === 'ativo').length;
              return ativosAguardando > 0 ? (
                <span className="ml-2 bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {ativosAguardando}
                </span>
              ) : null;
            })()}
          </TabsTrigger>
          <TabsTrigger value="volunteers">Voluntários/Líderes</TabsTrigger>
        </TabsList>

        <TabsContent value="groups">
          {/* Action Buttons */}
          <div className="flex gap-3 mb-6">
            <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary flex items-center space-x-2" data-testid="add-group-button">
                  <Plus size={20} />
                  <span>Novo PG</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Criar Novo Pequeno Grupo</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateGroup} className="space-y-4" data-testid="group-form">
                  <div>
                    <Label>Nome do Grupo</Label>
                    <Input
                      value={newGroup.name}
                      onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                      required
                      placeholder="Ex: PG Meninas Alfa"
                      data-testid="group-name-input"
                    />
                  </div>
                  <div>
                    <Label>Gênero</Label>
                    <Select value={newGroup.gender} onValueChange={(value) => setNewGroup({...newGroup, gender: value})}>
                      <SelectTrigger data-testid="group-gender-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="masculino">Masculino</SelectItem>
                        <SelectItem value="feminino">Feminino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                    <strong>💡 Faixa etária:</strong> Será calculada automaticamente com base na média de idade dos integrantes do grupo.
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button type="button" variant="outline" onClick={() => setGroupDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="btn-primary" data-testid="group-submit-button">Criar</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <Button
              onClick={() => setConfigDialogOpen(true)}
              className="btn-secondary flex items-center space-x-2"
              data-testid="auto-generate-button"
            >
              <Wand2 size={20} />
              <span>Gerar PGs Automaticamente</span>
            </Button>
          </div>

          {/* Filtros */}
          <div className="card mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Search size={18} className="text-gray-400" />
                <Input
                  placeholder="Buscar PG..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-48"
                  data-testid="pg-search-filter"
                />
              </div>
              
              <Select value={genderFilter} onValueChange={setGenderFilter}>
                <SelectTrigger className="w-40" data-testid="pg-gender-filter">
                  <SelectValue placeholder="Gênero" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="feminino">👧 Meninas</SelectItem>
                  <SelectItem value="masculino">👦 Meninos</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={ageRangeFilter} onValueChange={setAgeRangeFilter}>
                <SelectTrigger className="w-44" data-testid="pg-age-filter">
                  <SelectValue placeholder="Faixa Etária" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as idades</SelectItem>
                  <SelectItem value="10-12">10-12 anos</SelectItem>
                  <SelectItem value="13-14">13-14 anos</SelectItem>
                  <SelectItem value="15-16">15-16 anos</SelectItem>
                  <SelectItem value="17+">17+ anos</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={leaderStatusFilter} onValueChange={setLeaderStatusFilter}>
                <SelectTrigger className="w-36" data-testid="pg-leader-filter">
                  <SelectValue placeholder="Líder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="com_lider">Com Líder</SelectItem>
                  <SelectItem value="sem_lider">Sem Líder</SelectItem>
                </SelectContent>
              </Select>
              
              {(genderFilter !== 'all' || ageRangeFilter !== 'all' || searchFilter || leaderStatusFilter !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setGenderFilter('all');
                    setAgeRangeFilter('all');
                    setSearchFilter('');
                    setLeaderStatusFilter('all');
                  }}
                  className="text-gray-500"
                >
                  Limpar filtros
                </Button>
              )}
            </div>
          </div>

          {/* Groups Grid */}
          {loading ? (
            <div className="text-center py-12">Carregando...</div>
          ) : groups.length === 0 ? (
            <div className="card text-center py-12">
              <Users size={64} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg">Nenhum pequeno grupo criado ainda</p>
              <p className="text-gray-400 text-sm mt-2">Use os botões acima para criar grupos manualmente ou automaticamente</p>
            </div>
          ) : (() => {
            // Aplicar filtros
            const filteredGroups = groups.filter(group => {
              // Filtro por gênero
              if (genderFilter !== 'all' && group.gender !== genderFilter) return false;
              
              // Filtro por faixa etária
              if (ageRangeFilter !== 'all' && group.age_range) {
                const age = group.age_range;
                if (ageRangeFilter === '10-12' && (age < 10 || age > 12)) return false;
                if (ageRangeFilter === '13-14' && (age < 13 || age > 14)) return false;
                if (ageRangeFilter === '15-16' && (age < 15 || age > 16)) return false;
                if (ageRangeFilter === '17+' && age < 17) return false;
              }
              
              // Filtro por status de líder
              if (leaderStatusFilter !== 'all') {
                const hasLeader = group.leader_ids && group.leader_ids.length > 0;
                if (leaderStatusFilter === 'com_lider' && !hasLeader) return false;
                if (leaderStatusFilter === 'sem_lider' && hasLeader) return false;
              }
              
              // Filtro por busca
              if (searchFilter && !group.name.toLowerCase().includes(searchFilter.toLowerCase())) return false;
              
              return true;
            });
            
            // PGs atribuídos ao usuário logado
            const userAssignedPgIds = user?.assigned_pg_ids || [];
            const isUserPG = (groupId) => userAssignedPgIds.includes(groupId);
            
            // Função para ordenar: PGs do usuário primeiro
            const sortByUserPG = (a, b) => {
              const aIsUserPG = isUserPG(a.id);
              const bIsUserPG = isUserPG(b.id);
              if (aIsUserPG && !bIsUserPG) return -1;
              if (!aIsUserPG && bIsUserPG) return 1;
              return a.name.localeCompare(b.name);
            };
            
            // Agrupar por gênero e ordenar
            const girlsGroups = filteredGroups.filter(g => g.gender === 'feminino').sort(sortByUserPG);
            const boysGroups = filteredGroups.filter(g => g.gender === 'masculino').sort(sortByUserPG);
            
            const renderGroup = (group) => {
              const memberCount = group.member_ids?.length || 0;
              const isMale = group.gender === 'masculino';
              const isFemale = group.gender === 'feminino';
              const isFull = memberCount > 12;
              const hasVacancy = memberCount < 7;
              const hasNoLeader = !group.leader_ids || group.leader_ids.length === 0;
              const isMyPG = isUserPG(group.id);
              
              // Cores baseadas no gênero
              const cardBg = isMyPG
                ? 'bg-gradient-to-br from-purple-100 to-purple-200 border-purple-500 ring-2 ring-purple-400 ring-offset-2'
                : isMale 
                  ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300' 
                  : isFemale 
                    ? 'bg-gradient-to-br from-pink-50 to-pink-100 border-pink-300'
                    : 'bg-white border-gray-200';
              const headerColor = isMale ? 'text-blue-800' : isFemale ? 'text-pink-800' : 'text-gray-900';
              const accentColor = isMale ? 'text-blue-600' : isFemale ? 'text-pink-600' : 'text-gray-600';
              const genderIcon = isMale ? '👦' : isFemale ? '👧' : '👥';
              
              return (
                <div 
                  key={group.id} 
                  className={`card hover:shadow-xl transition border-2 ${cardBg} relative`} 
                  data-testid="group-card"
                >
                  {/* Meu PG Badge */}
                  {isMyPG && (
                    <div className="absolute -top-3 -left-3 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg z-10">
                      <Star size={12} fill="currentColor" />
                      Meu PG
                    </div>
                  )}
                  
                  {/* Status Badges */}
                  <div className="absolute -top-2 -right-2 flex gap-1 flex-wrap justify-end max-w-[180px]">
                    {hasNoLeader && (
                      <div className="bg-amber-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg" title="Grupo sem líder definido">
                        <AlertTriangle size={12} />
                        Sem Líder
                      </div>
                    )}
                    {isFull && (
                      <div className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg" title="Grupo lotado (mais de 12 membros)">
                        <AlertTriangle size={12} />
                        Lotado
                      </div>
                    )}
                    {hasVacancy && !isFull && (
                      <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg" title="Tem vagas disponíveis (menos de 7 membros)">
                        <UserCheck size={12} />
                        Vagas
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-start mb-3 pt-1">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{genderIcon}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className={`text-lg font-bold ${headerColor}`}>{group.name}</h3>
                          {group.is_temporary && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium flex items-center gap-1">
                              <Star size={10} />
                              Visitantes
                            </span>
                          )}
                        </div>
                        <p className={`text-xs ${accentColor}`}>
                          {group.age_range ? `${group.age_range} anos` : 'Sem membros'}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEditGroup(group)}
                        className={`p-1.5 rounded-lg transition ${isMale ? 'hover:bg-blue-200' : isFemale ? 'hover:bg-pink-200' : 'hover:bg-gray-100'}`}
                        title="Editar grupo"
                        data-testid="edit-group-button"
                      >
                        <Edit size={16} className={accentColor} />
                      </button>
                      <button
                        onClick={() => handleDeleteGroup(group.id)}
                        className="p-1.5 hover:bg-red-100 rounded-lg transition"
                        title="Excluir grupo"
                        data-testid="delete-group-button"
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className={`flex flex-col items-center p-2 rounded-lg ${isMale ? 'bg-blue-200/50' : isFemale ? 'bg-pink-200/50' : 'bg-gray-100'}`}>
                      <span className={`text-2xl font-bold ${isFull ? 'text-red-600' : hasVacancy ? 'text-green-600' : accentColor}`}>
                        {memberCount}
                        </span>
                        <span className="text-xs text-gray-600">Membros</span>
                      </div>
                      <div className={`flex flex-col items-center p-2 rounded-lg ${isMale ? 'bg-blue-200/50' : isFemale ? 'bg-pink-200/50' : 'bg-gray-100'}`}>
                        <span className={`text-2xl font-bold ${accentColor}`}>
                          {group.leader_ids?.length || 0}
                        </span>
                        <span className="text-xs text-gray-600">Líderes</span>
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => handleViewGroup(group)}
                      variant="outline"
                      className={`w-full flex items-center justify-center space-x-2 ${isMale ? 'border-blue-300 text-blue-700 hover:bg-blue-200' : isFemale ? 'border-pink-300 text-pink-700 hover:bg-pink-200' : ''}`}
                      data-testid="view-group-button"
                    >
                      <Eye size={16} />
                      <span>Ver Detalhes</span>
                    </Button>
                  </div>
                );
              };
              
            return filteredGroups.length === 0 ? (
              <div className="card text-center py-12">
                <Users size={64} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 text-lg">Nenhum PG encontrado com os filtros aplicados</p>
                <p className="text-gray-400 text-sm mt-2">Tente ajustar os filtros acima</p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Seção de Meninas */}
                {girlsGroups.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-2xl">👧</span>
                      <h2 className="text-xl font-bold text-pink-700">PGs de Meninas</h2>
                      <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm font-medium">
                        {girlsGroups.length} grupo{girlsGroups.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {girlsGroups.map(renderGroup)}
                    </div>
                  </div>
                )}
                
                {/* Seção de Meninos */}
                {boysGroups.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-2xl">👦</span>
                      <h2 className="text-xl font-bold text-blue-700">PGs de Meninos</h2>
                      <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                        {boysGroups.length} grupo{boysGroups.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {boysGroups.map(renderGroup)}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="sem-pg">
          {/* Aba para adolescentes sem PG */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Adolescentes Ativos Sem PG</h2>
                <p className="text-sm text-gray-600">
                  {pgStats.adolescentesSemPG} adolescente(s) ativo(s) ainda não alocado(s) em nenhum Pequeno Grupo
                </p>
              </div>
            </div>
            
            {/* Filtros */}
            <div className="card">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Search size={18} className="text-gray-400" />
                  <Input
                    placeholder="Buscar adolescente..."
                    value={semPgSearchFilter}
                    onChange={(e) => setSemPgSearchFilter(e.target.value)}
                    className="w-56"
                    data-testid="sem-pg-search-filter"
                  />
                </div>
                
                <Select value={semPgGenderFilter} onValueChange={setSemPgGenderFilter}>
                  <SelectTrigger className="w-40" data-testid="sem-pg-gender-filter">
                    <SelectValue placeholder="Gênero" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="feminino">👧 Meninas</SelectItem>
                    <SelectItem value="masculino">👦 Meninos</SelectItem>
                  </SelectContent>
                </Select>
                
                {(semPgSearchFilter || semPgGenderFilter !== 'all') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSemPgSearchFilter('');
                      setSemPgGenderFilter('all');
                    }}
                    className="text-gray-500"
                  >
                    Limpar filtros
                  </Button>
                )}
              </div>
            </div>
            
            {/* Lista de Adolescentes sem PG */}
            {(() => {
              // Função para identificar PGs de visitantes (mesmo critério do calculatePGStats)
              const isVisitantesPG = (g) => g.is_temporary || g.name?.toLowerCase().includes('visitante');
              
              // Criar set de IDs de membros em PGs REGULARES (não temporários/visitantes)
              const membersInRegularPGs = new Set();
              groups
                .filter(group => !isVisitantesPG(group)) // Ignora PGs temporários e de visitantes
                .forEach(group => {
                  (group.member_ids || []).forEach(id => membersInRegularPGs.add(id));
                });
              
              // Filtrar apenas ATIVOS sem PG REGULAR (podem estar em PG temporário/visitantes)
              const adolescentesSemPG = adolescents
                .filter(a => a.engagement_status === 'ativo' && !membersInRegularPGs.has(a.id))
                .filter(a => {
                  if (semPgGenderFilter !== 'all' && a.gender !== semPgGenderFilter) return false;
                  if (semPgSearchFilter && !a.name.toLowerCase().includes(semPgSearchFilter.toLowerCase())) return false;
                  return true;
                })
                .sort((a, b) => a.name.localeCompare(b.name));
              
              // Separar por gênero
              const meninas = adolescentesSemPG.filter(a => a.gender === 'feminino');
              const meninos = adolescentesSemPG.filter(a => a.gender === 'masculino');
              
              if (adolescentesSemPG.length === 0) {
                return (
                  <div className="card text-center py-12">
                    <UserCheck size={64} className="mx-auto mb-4 text-green-300" />
                    <p className="text-gray-500 text-lg">
                      {pgStats.adolescentesSemPG === 0 
                        ? 'Todos os adolescentes ativos já estão em um PG!' 
                        : 'Nenhum adolescente encontrado com os filtros aplicados'}
                    </p>
                  </div>
                );
              }
              
              return (
                <div className="space-y-6">
                  {/* Seção de Meninas */}
                  {meninas.length > 0 && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-2xl">👧</span>
                        <h3 className="text-lg font-bold text-pink-700">Meninas sem PG</h3>
                        <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm font-medium">
                          {meninas.length}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {meninas.map(adolescent => (
                          <div 
                            key={adolescent.id} 
                            className="card p-4 bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200 hover:shadow-md transition"
                            data-testid="sem-pg-card"
                          >
                            <div className="flex items-center gap-3">
                              {adolescent.photo_url ? (
                                <img src={adolescent.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-pink-200 flex items-center justify-center">
                                  <Users size={24} className="text-pink-500" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-800 truncate">{adolescent.name}</p>
                                <p className="text-sm text-pink-600">{adolescent.age} anos</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Seção de Meninos */}
                  {meninos.length > 0 && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-2xl">👦</span>
                        <h3 className="text-lg font-bold text-blue-700">Meninos sem PG</h3>
                        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                          {meninos.length}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {meninos.map(adolescent => (
                          <div 
                            key={adolescent.id} 
                            className="card p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-md transition"
                            data-testid="sem-pg-card"
                          >
                            <div className="flex items-center gap-3">
                              {adolescent.photo_url ? (
                                <img src={adolescent.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center">
                                  <Users size={24} className="text-blue-500" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-800 truncate">{adolescent.name}</p>
                                <p className="text-sm text-blue-600">{adolescent.age} anos</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="volunteers">
          <div className="flex justify-between items-center mb-6">
            <p className="text-gray-600">{volunteers.length} voluntário(s) cadastrado(s)</p>
            <Dialog open={volunteerDialogOpen} onOpenChange={setVolunteerDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary flex items-center space-x-2" data-testid="add-volunteer-button">
                  <Plus size={20} />
                  <span>Novo Voluntário</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cadastrar Voluntário/Líder</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateVolunteer} className="space-y-4" data-testid="volunteer-form">
                  <div>
                    <Label>Nome Completo *</Label>
                    <Input
                      value={newVolunteer.name}
                      onChange={(e) => setNewVolunteer({...newVolunteer, name: e.target.value})}
                      required
                      data-testid="volunteer-name-input"
                    />
                  </div>
                  <div>
                    <Label>Telefone *</Label>
                    <Input
                      value={newVolunteer.phone}
                      onChange={(e) => setNewVolunteer({...newVolunteer, phone: e.target.value})}
                      required
                      data-testid="volunteer-phone-input"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newVolunteer.email}
                      onChange={(e) => setNewVolunteer({...newVolunteer, email: e.target.value})}
                      data-testid="volunteer-email-input"
                    />
                  </div>
                  <div>
                    <Label>Idade</Label>
                    <Input
                      type="number"
                      value={newVolunteer.age || ''}
                      onChange={(e) => setNewVolunteer({...newVolunteer, age: e.target.value ? parseInt(e.target.value) : null})}
                      data-testid="volunteer-age-input"
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button type="button" variant="outline" onClick={() => setVolunteerDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="btn-primary" data-testid="volunteer-submit-button">Cadastrar</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {volunteers.length === 0 ? (
            <div className="card text-center py-12">
              <Users size={64} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg">Nenhum voluntário cadastrado</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Telefone</th>
                    <th>Email</th>
                    <th>Idade</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {volunteers.map((volunteer) => (
                    <tr key={volunteer.id} data-testid="volunteer-row">
                      <td className="font-semibold">{volunteer.name}</td>
                      <td>{volunteer.phone}</td>
                      <td>{volunteer.email || '-'}</td>
                      <td>{volunteer.age ? `${volunteer.age} anos` : '-'}</td>
                      <td>
                        <span className={`badge ${volunteer.status === 'ativo' ? 'badge-membro' : 'badge-checked_out'}`}>
                          {volunteer.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Aba de Controle de Imersão */}
        <TabsContent value="immersion">
          {(() => {
            // Calcula a lista filtrada uma vez para usar em toda a aba
            const filteredImmersion = pendingImmersion.filter(a => {
              const matchesSearch = a.name?.toLowerCase().includes(immersionSearchFilter.toLowerCase());
              const matchesGender = immersionGenderFilter === 'all' || a.gender === immersionGenderFilter;
              const matchesEngagement = 
                immersionEngagementFilter === 'all' || 
                immersionEngagementFilter === a.engagement_status ||
                (immersionEngagementFilter === 'ativo_desengajado' && (a.engagement_status === 'ativo' || a.engagement_status === 'desengajado'));
              return matchesSearch && matchesGender && matchesEngagement;
            });
            
            const allSelected = filteredImmersion.length > 0 && filteredImmersion.every(a => selectedForImmersion.includes(a.id));
            
            // Contagem por engajamento
            const countAtivos = pendingImmersion.filter(a => a.engagement_status === 'ativo').length;
            const countDesengajados = pendingImmersion.filter(a => a.engagement_status === 'desengajado').length;
            const countInativos = pendingImmersion.filter(a => a.engagement_status === 'inativo').length;
            
            return (
              <div className="space-y-6">
                {/* Header com ações */}
                <div className="flex flex-wrap gap-3 items-center justify-between">
                  <div className="flex gap-3 items-center flex-wrap">
                    <Button 
                      onClick={handleInitializeTemporaryPGs}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Star size={16} />
                      Criar PGs Visitantes
                    </Button>
                    
                    <Dialog open={markImmersionDialogOpen} onOpenChange={setMarkImmersionDialogOpen}>
                      <DialogTrigger asChild>
                        <Button 
                          className="btn-primary flex items-center gap-2"
                          disabled={selectedForImmersion.length === 0}
                        >
                          <CheckCircle2 size={16} />
                          Marcar Imersão ({selectedForImmersion.length})
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Marcar Imersão Concluída</DialogTitle>
                        </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Você está prestes a marcar <strong>{selectedForImmersion.length}</strong> adolescente(s) como tendo completado a Imersão Pulse. 
                        Eles serão automaticamente atualizados para status "membro".
                      </p>
                      <div>
                        <Label>Data da Imersão *</Label>
                        <Input 
                          type="date" 
                          value={immersionDate} 
                          onChange={(e) => setImmersionDate(e.target.value)}
                          required
                        />
                      </div>
                      <div className="flex gap-3 justify-end">
                        <Button variant="outline" onClick={() => setMarkImmersionDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleMarkImmersion} className="btn-primary">
                          Confirmar Imersão
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* Botão de Exportar */}
                <Button 
                  onClick={() => handleExportImmersionList(filteredImmersion, 'xlsx')}
                  variant="outline"
                  className="flex items-center gap-2"
                  disabled={filteredImmersion.length === 0}
                >
                  <Download size={16} />
                  Exportar ({filteredImmersion.length})
                </Button>
              </div>
            </div>

            {/* Cards de contagem - Ênfase nos Ativos */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-500 rounded-lg p-4 text-center shadow-sm">
                <p className="text-3xl font-bold text-green-600">{countAtivos}</p>
                <p className="text-sm font-medium text-green-700">Ativos Aguardando</p>
                <p className="text-xs text-green-600 mt-1">Prioridade para imersão</p>
              </div>
              <div className="bg-white border rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{countDesengajados}</p>
                <p className="text-sm text-gray-500">Desengajados</p>
              </div>
              <div className="bg-white border rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{countInativos}</p>
                <p className="text-sm text-gray-500">Inativos</p>
              </div>
              <div className="bg-white border rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-600">{pendingImmersion.length}</p>
                <p className="text-sm text-gray-500">Total Geral</p>
              </div>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded-lg">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Buscar por nome..."
                    value={immersionSearchFilter}
                    onChange={(e) => setImmersionSearchFilter(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={immersionGenderFilter} onValueChange={setImmersionGenderFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Gênero" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos Gêneros</SelectItem>
                  <SelectItem value="masculino">Meninos</SelectItem>
                  <SelectItem value="feminino">Meninas</SelectItem>
                </SelectContent>
              </Select>
              <Select value={immersionEngagementFilter} onValueChange={setImmersionEngagementFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Engajamento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos ({pendingImmersion.length})</SelectItem>
                  <SelectItem value="ativo">Ativos ({countAtivos})</SelectItem>
                  <SelectItem value="desengajado">Desengajados ({countDesengajados})</SelectItem>
                  <SelectItem value="ativo_desengajado">Ativos + Desengajados ({countAtivos + countDesengajados})</SelectItem>
                  <SelectItem value="inativo">Inativos ({countInativos})</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="text-sm font-medium text-purple-600">
                {filteredImmersion.length} exibido(s)
              </div>
            </div>

            {/* Lista de adolescentes pendentes de imersão */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left w-12">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => handleSelectAllForImmersion(filteredImmersion)}
                        className="w-4 h-4 rounded"
                      />
                    </th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Nome</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Idade</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Gênero</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Presenças</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-600">Engajamento</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredImmersion.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-gray-500">
                        {pendingImmersion.length === 0 
                          ? "🎉 Todos os adolescentes já fizeram a imersão!"
                          : "Nenhum adolescente encontrado com os filtros aplicados"
                        }
                      </td>
                    </tr>
                  ) : (
                    filteredImmersion.map(adolescent => (
                      <tr 
                        key={adolescent.id} 
                        className={`border-t hover:bg-gray-50 cursor-pointer ${selectedForImmersion.includes(adolescent.id) ? 'bg-purple-50' : ''}`}
                        onClick={() => handleToggleImmersionSelection(adolescent.id)}
                      >
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedForImmersion.includes(adolescent.id)}
                            onChange={() => {}}
                            className="w-4 h-4 rounded"
                          />
                        </td>
                        <td className="p-3 font-medium">{adolescent.name}</td>
                        <td className="p-3">{adolescent.age} anos</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${adolescent.gender === 'masculino' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                            {adolescent.gender === 'masculino' ? '♂ Masculino' : '♀ Feminino'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            adolescent.status === 'membro' ? 'bg-green-100 text-green-700' : 
                            adolescent.status === 'visitante' ? 'bg-amber-100 text-amber-700' : 
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {adolescent.status}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="font-medium text-gray-700">{adolescent.total_checkins || 0}</span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            adolescent.engagement_status === 'ativo' ? 'bg-green-100 text-green-700' : 
                            adolescent.engagement_status === 'desengajado' ? 'bg-amber-100 text-amber-700' : 
                            'bg-red-100 text-red-700'
                          }`}>
                            {adolescent.engagement_status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Info sobre PGs Temporários */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-medium text-purple-800 flex items-center gap-2 mb-2">
                <Star size={16} />
                PGs de Visitantes (Temporários)
              </h4>
              <p className="text-sm text-purple-700 mb-3">
                Os PGs de visitantes são grupos temporários para adolescentes que ainda não fizeram a imersão. 
                Após a imersão, eles devem ser removidos manualmente e adicionados a um PG regular.
              </p>
              {(() => {
                const temporaryPGs = groups.filter(g => g.is_temporary);
                if (temporaryPGs.length === 0) {
                  return (
                    <p className="text-sm text-purple-600 italic">
                      Nenhum PG de visitantes criado. Clique no botão "Criar PGs Visitantes" acima.
                    </p>
                  );
                }
                return (
                  <div className="flex gap-4">
                    {temporaryPGs.map(pg => (
                      <div key={pg.id} className="bg-white rounded-lg p-3 border border-purple-200 flex-1">
                        <p className="font-medium text-purple-800">{pg.name}</p>
                        <p className="text-sm text-purple-600">{pg.member_ids?.length || 0} membros</p>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* Configuration Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configurações de Geração Automática</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mínimo de Membros por PG</Label>
                <Input
                  type="number"
                  min="2"
                  max="10"
                  value={autoGenConfig.min_members_per_group}
                  onChange={(e) => setAutoGenConfig({...autoGenConfig, min_members_per_group: parseInt(e.target.value)})}
                  data-testid="min-members-input"
                />
                <p className="text-xs text-gray-500 mt-1">Mínimo: 2-5</p>
              </div>
              
              <div>
                <Label>Máximo de Membros por PG</Label>
                <Input
                  type="number"
                  min="3"
                  max="15"
                  value={autoGenConfig.max_members_per_group}
                  onChange={(e) => setAutoGenConfig({...autoGenConfig, max_members_per_group: parseInt(e.target.value)})}
                  data-testid="max-members-input"
                />
                <p className="text-xs text-gray-500 mt-1">Máximo: 6-12</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="separate-by-gender"
                  checked={autoGenConfig.separate_by_gender}
                  onChange={(e) => setAutoGenConfig({...autoGenConfig, separate_by_gender: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded"
                  data-testid="separate-gender-checkbox"
                />
                <Label htmlFor="separate-by-gender" className="cursor-pointer">
                  Separar por sexo (meninos e meninas em grupos diferentes)
                </Label>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                {autoGenConfig.separate_by_gender ? 'Grupos separados por gênero' : 'Grupos mistos permitidos'}
              </p>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="respect-affinity"
                  checked={autoGenConfig.respect_affinity}
                  onChange={(e) => setAutoGenConfig({...autoGenConfig, respect_affinity: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded"
                  data-testid="respect-affinity-checkbox"
                />
                <Label htmlFor="respect-affinity" className="cursor-pointer">
                  Respeitar afinidades entre adolescentes
                </Label>
              </div>
              <p className="text-xs text-gray-500 ml-6">Tenta agrupar adolescentes que se conhecem</p>
              
              {autoGenConfig.respect_affinity && (
                <div className="ml-6 mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="affinity-priority"
                      checked={autoGenConfig.affinity_priority}
                      onChange={(e) => setAutoGenConfig({...autoGenConfig, affinity_priority: e.target.checked})}
                      className="w-4 h-4 text-blue-600 rounded"
                      data-testid="affinity-priority-checkbox"
                    />
                    <Label htmlFor="affinity-priority" className="cursor-pointer text-sm">
                      🌟 Priorizar afinidades sobre idade
                    </Label>
                  </div>
                  <p className="text-xs text-blue-700 mt-1">
                    Agrupa amigos juntos mesmo que tenham idades diferentes (ex: 13-15 anos no mesmo grupo)
                  </p>
                </div>
              )}
            </div>

            {/* Filtro de Engajamento */}
            <div className="border-t pt-4">
              <Label className="text-sm font-semibold mb-3 block">Incluir adolescentes por engajamento:</Label>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="engagement-ativo"
                    checked={autoGenConfig.engagement_filter.includes('ativo')}
                    onChange={(e) => {
                      const newFilter = e.target.checked 
                        ? [...autoGenConfig.engagement_filter, 'ativo']
                        : autoGenConfig.engagement_filter.filter(f => f !== 'ativo');
                      setAutoGenConfig({...autoGenConfig, engagement_filter: newFilter});
                    }}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <Label htmlFor="engagement-ativo" className="cursor-pointer flex items-center">
                    <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                    Ativos
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="engagement-desengajado"
                    checked={autoGenConfig.engagement_filter.includes('desengajado')}
                    onChange={(e) => {
                      const newFilter = e.target.checked 
                        ? [...autoGenConfig.engagement_filter, 'desengajado']
                        : autoGenConfig.engagement_filter.filter(f => f !== 'desengajado');
                      setAutoGenConfig({...autoGenConfig, engagement_filter: newFilter});
                    }}
                    className="w-4 h-4 text-amber-600 rounded"
                  />
                  <Label htmlFor="engagement-desengajado" className="cursor-pointer flex items-center">
                    <span className="w-3 h-3 rounded-full bg-amber-500 mr-2"></span>
                    Desengajados
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="engagement-inativo"
                    checked={autoGenConfig.engagement_filter.includes('inativo')}
                    onChange={(e) => {
                      const newFilter = e.target.checked 
                        ? [...autoGenConfig.engagement_filter, 'inativo']
                        : autoGenConfig.engagement_filter.filter(f => f !== 'inativo');
                      setAutoGenConfig({...autoGenConfig, engagement_filter: newFilter});
                    }}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <Label htmlFor="engagement-inativo" className="cursor-pointer flex items-center">
                    <span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
                    Inativos
                  </Label>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {autoGenConfig.engagement_filter.length === 0 
                  ? '⚠️ Selecione pelo menos um tipo de engajamento'
                  : `Incluindo: ${autoGenConfig.engagement_filter.map(f => f === 'ativo' ? 'Ativos' : f === 'desengajado' ? 'Desengajados' : 'Inativos').join(', ')}`
                }
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Idade Mínima (opcional)</Label>
                <Input
                  type="number"
                  min="10"
                  max="18"
                  value={autoGenConfig.min_age || ''}
                  onChange={(e) => setAutoGenConfig({...autoGenConfig, min_age: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="Ex: 13"
                  data-testid="min-age-input"
                />
              </div>
              <div>
                <Label>Idade Máxima (opcional)</Label>
                <Input
                  type="number"
                  min="10"
                  max="18"
                  value={autoGenConfig.max_age || ''}
                  onChange={(e) => setAutoGenConfig({...autoGenConfig, max_age: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="Ex: 17"
                  data-testid="max-age-input"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>Cancelar</Button>
              <Button className="btn-primary" onClick={handleAutoGenerate} data-testid="generate-with-config">
                <Wand2 size={18} className="mr-2" />
                Gerar Preview
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Preview Group Dialog */}
      <Dialog open={editPreviewDialogOpen} onOpenChange={setEditPreviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Grupo do Preview</DialogTitle>
          </DialogHeader>
          {editingPreviewIndex !== null && preview[editingPreviewIndex] && (
            <EditPreviewGroup
              group={preview[editingPreviewIndex]}
              allAdolescents={adolescents}
              volunteers={volunteers}
              maxMembers={autoGenConfig.max_members_per_group}
              onSave={handleUpdatePreviewGroup}
              onCancel={() => setEditPreviewDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview de Grupos Gerados Automaticamente</DialogTitle>
          </DialogHeader>
          
          {preview.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Nenhum grupo para gerar</p>
          ) : (
            <>
              <div className="mb-4 space-y-1">
                <p className="text-gray-600">
                  {preview.length} grupo(s) será(ão) criado(s)
                  {preview.filter(g => g.has_affinity).length > 0 && (
                    <span className="ml-2 text-blue-600">
                      ({preview.filter(g => g.has_affinity).length} por afinidade, {preview.filter(g => !g.has_affinity).length} por idade)
                    </span>
                  )}
                </p>
                {previewMeta?.candidates_count != null && (
                  <p className="text-sm text-gray-500">
                    Base usada: <span className="font-medium">{previewMeta.candidates_count} adolescentes</span>
                    {previewMeta.candidate_criteria ? ` — ${previewMeta.candidate_criteria}` : ''}
                  </p>
                )}
              </div>
              <div className="space-y-4">
                {preview.map((group, index) => (
                  <div 
                    key={index} 
                    className={`border-2 rounded-lg p-4 hover:border-blue-300 transition ${group.has_affinity ? 'border-blue-400 bg-blue-50/50' : ''}`}
                    data-testid="preview-group"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-lg">{group.suggested_name || group.name}</h4>
                          {group.has_affinity && (
                            <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">🌟 Afinidade</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {group.member_count} membro(s) • {group.age_label || group.age_range} anos • {group.gender === 'masculino' ? 'Meninos' : group.gender === 'feminino' ? 'Meninas' : 'Misto'}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSaveSinglePreviewGroup(index)}
                          className="p-2 hover:bg-green-50 rounded-lg transition"
                          title="Salvar este grupo"
                        >
                          <Check size={18} className="text-green-600" />
                        </button>
                        <button
                          onClick={() => handleEditPreviewGroup(index)}
                          className="p-2 hover:bg-blue-50 rounded-lg transition"
                          title="Editar grupo"
                        >
                          <Edit size={18} className="text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleRemovePreviewGroup(index)}
                          className="p-2 hover:bg-red-50 rounded-lg transition"
                          title="Remover grupo"
                        >
                          <XIcon size={18} className="text-red-600" />
                        </button>
                      </div>
                    </div>
                    
                    {group.leader_ids && group.leader_ids.length > 0 && (
                      <div className="mb-3 p-2 bg-purple-50 rounded">
                        <p className="text-xs font-semibold text-purple-700 mb-1">Líderes:</p>
                        <div className="flex flex-wrap gap-1">
                          {group.leader_ids.map((leaderId) => {
                            const leader = volunteers.find(v => v.id === leaderId);
                            return leader ? (
                              <span key={leaderId} className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded">
                                {leader.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-gray-50 rounded p-3">
                      <p className="text-sm font-semibold mb-2">Membros:</p>
                      <div className="flex flex-wrap gap-2">
                        {[...group.members].sort((a, b) => a.name.localeCompare(b.name)).map((member) => (
                          <span key={member.id} className="badge badge-membro">{member.name}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>Cancelar</Button>
                <Button className="btn-primary" onClick={handleCreateFromPreview} data-testid="confirm-create-groups">
                  Criar {preview.length} Grupo(s)
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* View Group Details Dialog */}
      <Dialog open={viewGroupDialogOpen} onOpenChange={setViewGroupDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedGroupDetails?.name}</DialogTitle>
          </DialogHeader>
          
          {selectedGroupDetails && (
            <div className="space-y-4">
              {/* Group Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <strong>Faixa Etária:</strong> {selectedGroupDetails.age_range} anos • 
                  <strong className="ml-2">Gênero:</strong> {selectedGroupDetails.gender === 'masculino' ? 'Meninos' : selectedGroupDetails.gender === 'feminino' ? 'Meninas' : 'Misto'}
                </p>
              </div>

              {/* Tabs for Manage/Attendance */}
              <Tabs defaultValue="manage">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="manage">Gerenciar Grupo</TabsTrigger>
                  <TabsTrigger value="attendance">Presença</TabsTrigger>
                </TabsList>

                <TabsContent value="manage">
                  <div className="space-y-6">
                    {/* Leaders Section */}
              <div>
                <div className="mb-3">
                  <h4 className="font-bold text-lg mb-2">Líderes ({selectedGroupDetails.leaders?.length || 0})</h4>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      placeholder="Buscar líder para adicionar..."
                      value={leaderSearchModal}
                      onChange={(e) => setLeaderSearchModal(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {leaderSearchModal && (
                    <div className="mt-2 max-h-32 overflow-y-auto border rounded-lg">
                      {volunteers
                        .filter(v => v.is_pg_leader && v.status === 'ativo')
                        .filter(v => !selectedGroupDetails.leader_ids?.includes(v.id))
                        .filter(v => v.name.toLowerCase().includes(leaderSearchModal.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .slice(0, 5)
                        .map((volunteer) => (
                          <div
                            key={volunteer.id}
                            className="flex items-center justify-between p-2 hover:bg-purple-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => { handleAddLeaderToGroup(volunteer.id); setLeaderSearchModal(''); }}
                          >
                            <span className="font-medium">{volunteer.name}</span>
                            <Plus size={16} className="text-purple-600" />
                          </div>
                        ))}
                      {volunteers.filter(v => v.is_pg_leader && v.status === 'ativo' && !selectedGroupDetails.leader_ids?.includes(v.id) && v.name.toLowerCase().includes(leaderSearchModal.toLowerCase())).length === 0 && (
                        <p className="text-gray-500 text-center py-2 text-sm">Nenhum líder encontrado</p>
                      )}
                    </div>
                  )}
                </div>
                
                {selectedGroupDetails.leaders && selectedGroupDetails.leaders.length > 0 ? (
                  <div className="space-y-2">
                    {[...selectedGroupDetails.leaders].sort((a, b) => a.name.localeCompare(b.name)).map((leader) => (
                      <div key={leader.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                        <div>
                          <p className="font-semibold">{leader.name}</p>
                          <p className="text-sm text-gray-600">{leader.phone}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveLeaderFromGroup(leader.id)}
                          className="p-2 hover:bg-red-100 rounded-lg transition"
                        >
                          <UserMinus size={18} className="text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nenhum líder atribuído</p>
                )}
              </div>

              {/* Facilitators Section */}
              <div>
                <div className="mb-3">
                  <h4 className="font-bold text-lg mb-2">Facilitadores ({selectedGroupDetails.facilitators?.length || 0})</h4>
                  <p className="text-sm text-gray-500 mb-2">Voluntários da área "Facilitadores" que apoiam este PG</p>
                </div>
                
                {selectedGroupDetails.facilitators && selectedGroupDetails.facilitators.length > 0 ? (
                  <div className="space-y-2">
                    {[...selectedGroupDetails.facilitators].sort((a, b) => a.name.localeCompare(b.name)).map((facilitator) => (
                      <div key={facilitator.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <div>
                          <p className="font-semibold">{facilitator.name}</p>
                          <p className="text-sm text-gray-600">{facilitator.phone}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nenhum facilitador atribuído</p>
                )}
              </div>

              {/* Members Section */}
              <div>
                <div className="mb-3">
                  <h4 className="font-bold text-lg mb-2">Membros ({selectedGroupDetails.members?.length || 0})</h4>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      placeholder="Buscar adolescente para adicionar..."
                      value={memberSearchModal}
                      onChange={(e) => setMemberSearchModal(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {memberSearchModal && (
                    <div className="mt-2 max-h-32 overflow-y-auto border rounded-lg">
                      {adolescents
                        .filter(a => 
                          a.engagement_status !== 'inativo' &&
                          !selectedGroupDetails.member_ids?.includes(a.id)
                        )
                        .filter(a => a.name.toLowerCase().includes(memberSearchModal.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .slice(0, 5)
                        .map((adolescent) => (
                          <div
                            key={adolescent.id}
                            className="flex items-center justify-between p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => { handleAddMemberToGroup(adolescent.id); setMemberSearchModal(''); }}
                          >
                            <div className="flex items-center gap-2">
                              {/* Foto do adolescente */}
                              {adolescent.photo_url ? (
                                <img 
                                  src={getImageUrl(adolescent.photo_url)} 
                                  alt={adolescent.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs ${adolescent.gender === 'masculino' ? 'bg-blue-400' : 'bg-pink-400'}`}>
                                  {adolescent.name?.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <span className="font-medium">{adolescent.name}</span>
                              <span className="text-xs text-gray-500">{adolescent.age} anos</span>
                            </div>
                            <Plus size={16} className="text-blue-600" />
                          </div>
                        ))}
                      {adolescents.filter(a => 
                        a.engagement_status !== 'inativo' &&
                        !selectedGroupDetails.member_ids?.includes(a.id) &&
                        a.name.toLowerCase().includes(memberSearchModal.toLowerCase())
                      ).length === 0 && (
                        <p className="text-gray-500 text-center py-2 text-sm">Nenhum adolescente encontrado</p>
                      )}
                    </div>
                  )}
                </div>
                
                {selectedGroupDetails.members && selectedGroupDetails.members.length > 0 ? (
                  <div className="space-y-2">
                    {[...selectedGroupDetails.members].sort((a, b) => a.name.localeCompare(b.name)).map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {/* Foto do membro */}
                          {member.photo_url ? (
                            <img 
                              src={getImageUrl(member.photo_url)} 
                              alt={member.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                            />
                          ) : (
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${member.gender === 'masculino' ? 'bg-blue-500' : 'bg-pink-500'}`}>
                              {member.name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold">{member.name}</p>
                            <p className="text-sm text-gray-600">{member.age} anos • {member.phone}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveMemberFromGroup(member.id)}
                          className="p-2 hover:bg-red-100 rounded-lg transition"
                        >
                          <UserMinus size={18} className="text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nenhum membro no grupo</p>
                )}
              </div>
            </div>
          </TabsContent>

                <TabsContent value="attendance">
                  <PGAttendance 
                    group={selectedGroup} 
                    members={selectedGroupDetails.members || []}
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editGroupDialogOpen} onOpenChange={setEditGroupDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Pequeno Grupo</DialogTitle>
          </DialogHeader>
          
          {editingGroup && (
            <div className="space-y-6 mt-4">
              {/* Nome do Grupo */}
              <div>
                <Label className="text-sm font-semibold">Nome do Grupo</Label>
                <Input
                  value={editingGroup.name}
                  onChange={(e) => setEditingGroup({...editingGroup, name: e.target.value})}
                  className="mt-1"
                />
              </div>

              {/* Info básica */}
              <div className="grid grid-cols-3 gap-4 p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Média de Idade</p>
                  <p className="font-bold">
                    {editingGroup.age_range ? `${editingGroup.age_range} anos` : 'Sem membros'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Gênero</p>
                  <p className="font-bold">{editingGroup.gender === 'masculino' ? 'Meninos' : editingGroup.gender === 'feminino' ? 'Meninas' : 'Misto'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Membros</p>
                  <p className="font-bold text-blue-600">{editingGroup.member_ids?.length || 0}</p>
                </div>
              </div>

              {/* Líderes */}
              <div>
                <Label className="text-lg font-semibold mb-2 block">Líderes ({editingGroup.leader_ids?.length || 0})</Label>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Buscar líder para adicionar..."
                    value={editLeaderSearch}
                    onChange={(e) => setEditLeaderSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {editLeaderSearch && (
                  <div className="mb-3 max-h-32 overflow-y-auto border rounded-lg">
                    {volunteers
                      .filter(v => v.is_pg_leader && v.status === 'ativo')
                      .filter(v => !editingGroup.leader_ids.includes(v.id))
                      .filter(v => v.name.toLowerCase().includes(editLeaderSearch.toLowerCase()))
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .slice(0, 5)
                      .map((volunteer) => (
                        <div
                          key={volunteer.id}
                          className="flex items-center justify-between p-2 hover:bg-purple-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => { toggleEditLeader(volunteer.id); setEditLeaderSearch(''); }}
                        >
                          <span className="font-medium">{volunteer.name}</span>
                          <Plus size={16} className="text-purple-600" />
                        </div>
                      ))}
                  </div>
                )}
                
                {/* Líderes selecionados */}
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {volunteers
                    .filter(v => editingGroup.leader_ids.includes(v.id))
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((leader) => (
                      <div key={leader.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                        <div>
                          <p className="font-semibold">{leader.name}</p>
                          <p className="text-sm text-gray-600">{leader.phone}</p>
                        </div>
                        <button
                          onClick={() => toggleEditLeader(leader.id)}
                          className="p-2 hover:bg-red-100 rounded-lg transition"
                        >
                          <UserMinus size={18} className="text-red-600" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>

              {/* Membros */}
              <div>
                <Label className="text-lg font-semibold mb-2 block">Facilitadores ({editingGroup.facilitator_ids?.length || 0})</Label>
                <p className="text-sm text-gray-500 mb-3">Voluntários da área "Facilitadores" que apoiam este PG</p>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Buscar facilitador para adicionar..."
                    value={editFacilitatorSearch}
                    onChange={(e) => setEditFacilitatorSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {editFacilitatorSearch && (
                  <div className="mb-3 max-h-32 overflow-y-auto border rounded-lg">
                    {volunteers
                      .filter(v => v.status === 'ativo')
                      .filter(v => v.service_area_ids?.some(areaId => {
                        const area = serviceAreas?.find(a => a.id === areaId);
                        return area?.name?.toLowerCase().includes('facilitador');
                      }))
                      .filter(v => !(editingGroup.facilitator_ids || []).includes(v.id))
                      .filter(v => v.name.toLowerCase().includes(editFacilitatorSearch.toLowerCase()))
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .slice(0, 5)
                      .map((volunteer) => (
                        <div
                          key={volunteer.id}
                          className="flex items-center justify-between p-2 hover:bg-orange-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => { toggleEditFacilitator(volunteer.id); setEditFacilitatorSearch(''); }}
                        >
                          <span className="font-medium">{volunteer.name}</span>
                          <Plus size={16} className="text-orange-600" />
                        </div>
                      ))}
                    {volunteers.filter(v => v.status === 'ativo' && v.service_area_ids?.some(areaId => {
                      const area = serviceAreas?.find(a => a.id === areaId);
                      return area?.name?.toLowerCase().includes('facilitador');
                    }) && !(editingGroup.facilitator_ids || []).includes(v.id) && v.name.toLowerCase().includes(editFacilitatorSearch.toLowerCase())).length === 0 && (
                      <p className="p-2 text-sm text-gray-500 text-center">
                        {volunteers.filter(v => v.status === 'ativo' && v.service_area_ids?.some(areaId => {
                          const area = serviceAreas?.find(a => a.id === areaId);
                          return area?.name?.toLowerCase().includes('facilitador');
                        })).length === 0 
                          ? 'Nenhum facilitador cadastrado. Crie a área "Facilitadores" e vincule voluntários.'
                          : 'Nenhum facilitador encontrado'
                        }
                      </p>
                    )}
                  </div>
                )}
                
                {/* Facilitadores selecionados */}
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {volunteers
                    .filter(v => (editingGroup.facilitator_ids || []).includes(v.id))
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((facilitator) => (
                      <div key={facilitator.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <div>
                          <p className="font-semibold">{facilitator.name}</p>
                          <p className="text-sm text-gray-600">{facilitator.phone}</p>
                        </div>
                        <button
                          onClick={() => toggleEditFacilitator(facilitator.id)}
                          className="p-2 hover:bg-red-100 rounded-lg transition"
                        >
                          <UserMinus size={18} className="text-red-600" />
                        </button>
                      </div>
                    ))}
                  {(editingGroup.facilitator_ids?.length === 0 || !editingGroup.facilitator_ids) && (
                    <p className="text-sm text-gray-400 text-center py-2">Nenhum facilitador vinculado</p>
                  )}
                </div>
              </div>

              {/* Membros (Adolescentes) */}
              <div>
                <Label className="text-lg font-semibold mb-2 block">Membros ({editingGroup.members?.length || editingGroup.member_ids?.length || 0})</Label>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Buscar adolescente para adicionar..."
                    value={editMemberSearch}
                    onChange={(e) => setEditMemberSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {editMemberSearch && (
                  <div className="mb-3 max-h-32 overflow-y-auto border rounded-lg">
                    {adolescents
                      .filter(a => a.engagement_status !== 'inativo')
                      .filter(a => !editingGroup.member_ids.includes(a.id))
                      .filter(a => a.name.toLowerCase().includes(editMemberSearch.toLowerCase()))
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .slice(0, 8)
                      .map((adolescent) => (
                        <div
                          key={adolescent.id}
                          className="flex items-center justify-between p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => addEditMember(adolescent)}
                        >
                          <div className="flex items-center gap-2">
                            {/* Foto do adolescente */}
                            {adolescent.photo_url ? (
                              <img 
                                src={getImageUrl(adolescent.photo_url)} 
                                alt={adolescent.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs ${adolescent.gender === 'masculino' ? 'bg-blue-400' : 'bg-pink-400'}`}>
                                {adolescent.name?.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <span className="font-medium">{adolescent.name}</span>
                              <span className="text-gray-500 text-sm ml-2">{adolescent.age} anos</span>
                            </div>
                          </div>
                          <Plus size={16} className="text-blue-600" />
                        </div>
                      ))}
                    {adolescents.filter(a => 
                      a.engagement_status !== 'inativo' && 
                      !editingGroup.member_ids.includes(a.id) &&
                      a.name.toLowerCase().includes(editMemberSearch.toLowerCase())
                    ).length === 0 && (
                      <p className="text-gray-500 text-center py-2 text-sm">Nenhum adolescente encontrado</p>
                    )}
                  </div>
                )}
                
                {/* Membros do grupo */}
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {(editingGroup.members || [])
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {/* Foto do membro */}
                          {member.photo_url ? (
                            <img 
                              src={getImageUrl(member.photo_url)} 
                              alt={member.name}
                              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                            />
                          ) : (
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${member.gender === 'masculino' ? 'bg-blue-500' : 'bg-pink-500'}`}>
                              {member.name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold">{member.name}</p>
                            <p className="text-sm text-gray-600">{member.age} anos</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeEditMember(member.id)}
                          className="p-2 hover:bg-red-100 rounded-lg transition"
                        >
                          <UserMinus size={18} className="text-red-600" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>

              {/* Botões de ação */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditGroupDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveEditedGroup} className="btn-primary">
                  Salvar Alterações
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SmallGroups;
